import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Music2, Heart, Star, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PreviewButton } from "@/components/PreviewButton";
import { stopPreview } from "@/lib/audio";
import { AI_STYLES, AI_MOODS, AI_LANGUAGES, AI_ERAS } from "@/lib/constants";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
      active
        ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]"
        : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"
    }`}
  >
    {children}
  </button>
);

const SuggestionRow = ({ s, onSelect, onFavorite, isFav }) => (
  <div className="rounded-xl border border-[color:var(--brand-border)] bg-white p-2.5">
    <div className="flex items-start gap-3">
      <button
        type="button"
        data-testid="ai-suggestion-select"
        onClick={() => onSelect(s)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left transition-colors hover:bg-[color:var(--brand-paper)]"
        title="Escolher esta música"
      >
        {s.artwork ? (
          <img src={s.artwork} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-paper)]">
            <Music2 className="h-5 w-5 text-[color:var(--brand-muted)]" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[color:var(--brand-graphite)]">{s.track_name}</p>
          <p className="truncate text-xs text-[color:var(--brand-muted)]">
            {s.artist_name}
            {s.style ? ` · ${s.style}` : ""}
            {s.source === "popular" ? ` · Recomendação de outros clientes${s.count ? ` · escolhida ${s.count}x` : ""}` : ""}
            {s.source === "lc" ? " · Recomendação LC" : ""}
          </p>
          {s.rationale_ptbr && <p className="mt-0.5 line-clamp-2 text-[11px] italic text-[color:var(--brand-muted)]">{s.rationale_ptbr}</p>}
        </div>
      </button>
      <PreviewButton url={s.preview_url} testId={`ai-preview-${(s.track_name || "").slice(0, 8)}`} />
    </div>
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        onClick={() => onFavorite(s)}
        title="Favoritar"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[color:var(--brand-border)] bg-white hover:bg-[color:var(--brand-marsala-soft)]"
      >
        <Heart className={`h-4 w-4 ${isFav ? "fill-[color:var(--brand-marsala)] text-[color:var(--brand-marsala)]" : "text-[color:var(--brand-muted)]"}`} />
      </button>
      <Button
        onClick={() => onSelect(s)}
        className="h-9 flex-1 bg-[color:var(--brand-marsala)] text-sm text-white hover:bg-[color:var(--brand-marsala-hover)]"
      >
        Escolher esta música
      </Button>
    </div>
  </div>
);

export const AiAssistantModal = ({ open, onClose, momentName, onSelect, onFavorite, favorites = [] }) => {
  const [popular, setPopular] = useState([]);
  const [loadingPop, setLoadingPop] = useState(false);
  const [styles, setStyles] = useState([]);
  const [moods, setMoods] = useState([]);
  const [language, setLanguage] = useState("Indiferente");
  const [era, setEra] = useState("Indiferente");
  const [aiResults, setAiResults] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingPop(true);
      api
        .get("/suggestions/popular", { params: { moment: momentName } })
        .then(({ data }) => setPopular(data.suggestions || []))
        .catch(() => setPopular([]))
        .finally(() => setLoadingPop(false));
    }
  }, [open, momentName]);

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const generate = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post("/ai/suggestions", {
        moment: momentName,
        styles,
        moods,
        language,
        era,
      });
      const list = data.suggestions || [];
      setAiResults(list);
      if (list.length === 0) {
        toast.error("Não conseguimos gerar sugestões agora. Tente novamente em instantes.");
      }
    } catch {
      setAiResults([]);
      toast.error("Não conseguimos gerar sugestões agora. Tente novamente em instantes.");
    } finally {
      setLoadingAi(false);
    }
  };

  const isFav = (s) =>
    favorites.some((f) => f.track_name === s.track_name && f.artist_name === s.artist_name);

  const close = () => {
    stopPreview();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="ai-suggestions-modal" className="flex max-h-[90vh] w-[96vw] max-w-xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-[color:var(--brand-border)] p-4 sm:p-5">
          <DialogTitle className="flex items-center gap-2 font-serif text-lg sm:text-xl">
            <Sparkles className="h-5 w-5 text-[color:var(--brand-marsala)]" /> Sugestões para {momentName}
          </DialogTitle>
          <p className="text-sm text-[color:var(--brand-muted)]">Deixe a trilha desse momento ganhar forma.</p>
        </DialogHeader>

        <Tabs defaultValue="popular" className="flex min-h-0 flex-1 flex-col p-4 sm:p-5">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="popular" data-testid="ai-tab-popular">Mais escolhidas</TabsTrigger>
            <TabsTrigger value="ai" data-testid="ai-tab-style">Com o nosso estilo</TabsTrigger>
          </TabsList>

          <TabsContent value="popular" className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto">
            {loadingPop && (
              <div className="py-10 text-center text-sm text-[color:var(--brand-muted)]">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Carregando recomendações...
              </div>
            )}
            {!loadingPop && popular.length === 0 && (
              <div className="rounded-xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-6 text-center">
                <p className="text-sm text-[color:var(--brand-muted)]">
                  Ainda não há recomendações para este momento. Experimente "Com o nosso estilo".
                </p>
              </div>
            )}
            {!loadingPop &&
              popular.map((s, i) => (
                <SuggestionRow key={`${s.track_name}-${s.artist_name}-${s.source || ""}`} s={s} onSelect={onSelect} onFavorite={onFavorite} isFav={isFav(s)} />
              ))}
          </TabsContent>

          <TabsContent value="ai" className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {aiResults.length === 0 ? (
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-semibold text-[color:var(--brand-graphite)]">Qual estilo combina mais com vocês?</p>
                  <div className="flex flex-wrap gap-2">
                    {AI_STYLES.map((v) => (
                      <Chip key={v} active={styles.includes(v)} onClick={() => toggle(styles, setStyles, v)}>{v}</Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-[color:var(--brand-graphite)]">Qual sensação querem criar?</p>
                  <div className="flex flex-wrap gap-2">
                    {AI_MOODS.map((v) => (
                      <Chip key={v} active={moods.includes(v)} onClick={() => toggle(moods, setMoods, v)}>{v}</Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-[color:var(--brand-graphite)]">Preferência de idioma</p>
                  <div className="flex flex-wrap gap-2">
                    {AI_LANGUAGES.map((v) => (
                      <Chip key={v} active={language === v} onClick={() => setLanguage(v)}>{v}</Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-[color:var(--brand-graphite)]">Preferência de época</p>
                  <div className="flex flex-wrap gap-2">
                    {AI_ERAS.map((v) => (
                      <Chip key={v} active={era === v} onClick={() => setEra(v)}>{v}</Chip>
                    ))}
                  </div>
                </div>
                <Button
                  data-testid="ai-generate-button"
                  onClick={generate}
                  disabled={loadingAi}
                  className="h-11 w-full bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]"
                >
                  {loadingAi ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                  Encontrar músicas com o nosso estilo
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setAiResults([])}
                  className="mb-1 text-xs font-medium text-[color:var(--brand-marsala)] hover:underline"
                >
                  ← Ajustar preferências
                </button>
                {aiResults.map((s, i) => (
                  <SuggestionRow key={`${s.track_name}-${s.artist_name}-${s.source || ""}`} s={s} onSelect={onSelect} onFavorite={onFavorite} isFav={isFav(s)} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
